import { Route } from '@/Enums/Routes/Route.enum';
import { useAppSelector } from '@/store/hooks';
import { login } from '@/store/slices/authSlice';
import { EMAIL_RULES, PASSWORD_RULES } from '@/utils/validationRules';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Link,
  TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { NavLink } from 'react-router-dom';

const Login = () => {
  const { loading } = useAppSelector(state => state.auth);
  const { whitelabel } = useAppSelector(state => state.currentWhitelabel);

  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword(show => !show);
  const handleMouseDownPassword = event => {
    event.preventDefault();
  };

  const { control, handleSubmit } = useForm({
    defaultValues: {
      email: '',
      password: '',
      rememberMe: true,
    },
  });

  const onSubmit = data => {
    dispatch(login(data));
  };

  const loginDescriptionText = useMemo(() => {
    return `Let's login into your ${whitelabel?.whitelabelName || 'Ribeye'} account first`;
  }, [whitelabel?.whitelabelName]);

  return (
    <div className='xl:w-1/2'>
      <h1 className='font-bold text-[32px] text-[#121212] mb-[4px]'>Let&apos;s sign you in</h1>
      <div className='text-[#84818A] text-[14px] font-medium mb-[32px]'>{loginDescriptionText}</div>
      <form className='my-[32px]' onSubmit={handleSubmit(onSubmit)}>
        <FormLabel htmlFor='email-input'>Email</FormLabel>
        <Controller
          name='email'
          control={control}
          rules={EMAIL_RULES}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id='email-input'
              autoComplete='email'
              type='email'
              fullWidth
              error={!!error}
              helperText={error?.message}
              sx={{
                marginBottom: '16px',
              }}
              slotProps={{
                htmlInput: { placeholder: 'hi@example.com' },
              }}
            />
          )}
        />
        <FormLabel htmlFor='password-input'>Password</FormLabel>
        <Controller
          name='password'
          control={control}
          rules={PASSWORD_RULES}
          render={({ field, fieldState: { error } }) => (
            <TextField
              {...field}
              id='password-input'
              sx={{
                marginBottom: '16px',
              }}
              fullWidth
              error={!!error}
              helperText={error?.message}
              autoComplete='current-password'
              type={showPassword ? 'text' : 'password'}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        aria-label='toggle password visibility'
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge='end'
                      >
                        {showPassword ? <VisibilityOffOutlined /> : <VisibilityOutlined />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },

                htmlInput: { placeholder: 'Enter password' },
              }}
            />
          )}
        />
        <div className='flex justify-between items-center mb-[25px]'>
          <Controller
            name='rememberMe'
            control={control}
            render={({ field }) => (
              <FormControlLabel
                sx={{ marginLeft: '-9px' }}
                control={<Checkbox {...field} checked={field.value} />}
                label='Remember Me'
              />
            )}
          />
          <Link
            component={NavLink}
            disabled={loading}
            to={Route.FORGOT_PASSWORD}
            underline='hover'
            sx={{
              padding: 0,
              borderRadius: 0,
              lineHeight: 1,
              minWidth: 0,
              fontWeight: 700,
              fontSize: '14px',
            }}
          >
            Forgot Password
          </Link>
        </div>
        <Button
          sx={{
            height: 48,
            fontWeight: 700,
          }}
          type='submit'
          variant='contained'
          fullWidth
          disabled={loading}
        >
          {loading ? <CircularProgress size={20} color='primary' /> : 'Login'}
        </Button>
      </form>
    </div>
  );
};

export default Login;
